import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, CheckCircle, Mail, Phone, MapPin, Sparkles } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import GlassSelect from './GlassSelect';
import { triggerUFOAnimation } from './SubmissionUFOAnimation';

const LeadForm = ({ prefilledService = '' }) => {
  const { toast } = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    mobile: '',
    email: '',
    city: '',
    industry: '',
    service: prefilledService,
    budget: '',
    timeline: '',
    projectDetails: ''
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(formData.mobile.replace(/\s/g, ''))) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData.industry.trim()) {
      newErrors.industry = 'Industry is required';
    }

    if (!formData.service) {
      newErrors.service = 'Please select a service';
    }

    if (!formData.budget.trim()) {
      newErrors.budget = 'Budget is required';
    }

    if (!formData.timeline.trim()) {
      newErrors.timeline = 'Timeline is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const submitBtn = e.currentTarget.querySelector('button[type="submit"]') || e.target;
      
      // Trigger UFO animation & sparkle burst immediately while button is mounted
      triggerUFOAnimation(submitBtn || e);

      const leads = JSON.parse(localStorage.getItem('stellars_leads') || '[]');
      const newLead = {
        ...formData,
        timestamp: new Date().toISOString(),
        id: Date.now()
      };
      leads.push(newLead);
      localStorage.setItem('stellars_leads', JSON.stringify(leads));

      setIsSubmitted(true);
      toast({
        title: "Inquiry Submitted!",
        description: "Thank you! Our growth team will contact you within 4 hours.",
      });

      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({
          fullName: '',
          companyName: '',
          mobile: '',
          email: '',
          city: '',
          industry: '',
          service: '',
          budget: '',
          timeline: '',
          projectDetails: ''
        });
      }, 5000);
    }
  };

  React.useEffect(() => {
    if (prefilledService) {
      setFormData(prev => ({ ...prev, service: prefilledService }));
    }
  }, [prefilledService]);

  return (
    <section 
      id="contact"
      className="py-16 sm:py-20 lg:py-24 bg-[#181F32] relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left Column: Info & Value Prop */}
          <motion.div
            initial={{ opacity: 0, y: 35 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, ease: [0.215, 0.61, 0.355, 1] }}
            className="lg:col-span-5 text-white glass-panel-hero p-8"
          >
            <span className="glass-badge mb-3">
              Let's Scale Together
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-6 leading-tight font-heading">
              Ready to Accelerate Your <span className="text-[#F3453F]">Growth</span>?
            </h2>
            <p className="text-base sm:text-lg text-white/90 mb-8 font-sans leading-relaxed font-medium">
              Book a complimentary strategy consultation with our performance leads. We'll audit your current marketing funnel and map out a data-driven ROI plan.
            </p>

            <div className="space-y-6 font-sans mb-10">
              <div className="flex items-start gap-4">
                <div className="glass-icon-pod p-2.5 flex-shrink-0">
                  <Phone size={20} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/90 font-bold">Call or WhatsApp</p>
                  <p className="text-base font-extrabold text-white">+91 93109 71677</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="glass-icon-pod p-2.5 flex-shrink-0">
                  <Mail size={20} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/90 font-bold">Direct Email</p>
                  <p className="text-base font-extrabold text-white">connect@stellarsestates.in</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="glass-icon-pod p-2.5 flex-shrink-0">
                  <MapPin size={20} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-white/90 font-bold">Presence</p>
                  <p className="text-base font-extrabold text-white">Delhi/NCR, India</p>
                </div>
              </div>
            </div>

            <div className="glass-panel p-5">
              <div className="flex items-center gap-2 mb-2 text-[#F3453F] font-extrabold text-sm">
                <Sparkles size={18} />
                <span>4-Hour SLA Guarantee</span>
              </div>
              <p className="text-xs text-white/90 leading-relaxed font-sans font-medium">
                Our team reviews inquiries within 4 business hours. Confidentiality assured.
              </p>
            </div>
          </motion.div>

          {/* Right Column: Form */}
          <motion.div
            initial={{ opacity: 0, y: 35 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.215, 0.61, 0.355, 1] }}
            className="lg:col-span-7 glass-panel-white p-6 sm:p-10"
          >
            {isSubmitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="py-12 text-center"
              >
                <div className="glass-icon-pod w-16 h-16 p-4 mx-auto mb-4">
                  <CheckCircle size={36} />
                </div>
                <h3 className="text-2xl font-extrabold mb-2 text-[#181F32] font-heading">
                  Inquiry Submitted Successfully!
                </h3>
                <p className="text-[#181F32]/80 font-sans text-sm max-w-md mx-auto font-medium">
                  Thank you for connecting with Stellars Estates. Our growth strategists will review your project details and reach out shortly.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4 font-sans">
                <div className="mb-4">
                  <h3 className="text-2xl font-extrabold text-[#181F32] font-heading">
                    Request Free Consultation
                  </h3>
                  <p className="text-xs text-[#181F32]/70 font-sans mt-1 font-medium">
                    Fill in your project requirements for an instant strategy audit.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-[#181F32] uppercase tracking-wide mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      className="glass-input"
                      placeholder="Sahil Sharma"
                    />
                    {errors.fullName && <p className="text-[#F3453F] text-xs mt-1 font-bold">{errors.fullName}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#181F32] uppercase tracking-wide mb-1">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      className="glass-input"
                      placeholder="Your Business Name"
                    />
                    {errors.companyName && <p className="text-[#F3453F] text-xs mt-1 font-bold">{errors.companyName}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-[#181F32] uppercase tracking-wide mb-1">
                      Mobile Number *
                    </label>
                    <input
                      type="tel"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      className="glass-input"
                      placeholder="9876543210"
                    />
                    {errors.mobile && <p className="text-[#F3453F] text-xs mt-1 font-bold">{errors.mobile}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#181F32] uppercase tracking-wide mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="glass-input"
                      placeholder="sahil@example.com"
                    />
                    {errors.email && <p className="text-[#F3453F] text-xs mt-1 font-bold">{errors.email}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-[#181F32] uppercase tracking-wide mb-1">
                      City *
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="glass-input"
                      placeholder="e.g. Mumbai, Bangalore"
                    />
                    {errors.city && <p className="text-[#F3453F] text-xs mt-1 font-bold">{errors.city}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#181F32] uppercase tracking-wide mb-1">
                      Industry *
                    </label>
                    <input
                      type="text"
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      className="glass-input"
                      placeholder="Real Estate, E-Commerce, etc."
                    />
                    {errors.industry && <p className="text-[#F3453F] text-xs mt-1 font-bold">{errors.industry}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-[#181F32] uppercase tracking-wide mb-1">
                      Service Selected *
                    </label>
                    <GlassSelect
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      placeholder="Select a service"
                      options={[
                        { value: 'Ad Campaign', label: 'Ad Campaign (Meta/Google)' },
                        { value: 'Social Media Marketing', label: 'Social Media Marketing' },
                        { value: 'Website Development', label: 'Website Development' },
                        { value: 'WhatsApp Marketing', label: 'WhatsApp Marketing API' },
                        { value: 'SEO & AEO', label: 'SEO & Search Dominance' },
                        { value: 'Other', label: 'Custom Growth Plan' },
                      ]}
                    />
                    {errors.service && <p className="text-[#F3453F] text-xs mt-1 font-bold">{errors.service}</p>}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-[#181F32] uppercase tracking-wide mb-1">
                      Monthly Budget *
                    </label>
                    <input
                      type="text"
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      className="glass-input"
                      placeholder="e.g. ₹50,000 - ₹1,00,000"
                    />
                    {errors.budget && <p className="text-[#F3453F] text-xs mt-1 font-bold">{errors.budget}</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#181F32] uppercase tracking-wide mb-1">
                    Target Timeline *
                  </label>
                  <input
                    type="text"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleChange}
                    className="glass-input"
                    placeholder="e.g. Immediate, Within 2 weeks"
                  />
                  {errors.timeline && <p className="text-[#F3453F] text-xs mt-1 font-bold">{errors.timeline}</p>}
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#181F32] uppercase tracking-wide mb-1">
                    Project Details
                  </label>
                  <textarea
                    name="projectDetails"
                    value={formData.projectDetails}
                    onChange={handleChange}
                    rows="3"
                    className="glass-input resize-none"
                    placeholder="Tell us briefly about your growth goals or requirements..."
                  ></textarea>
                </div>

                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className="w-full mt-4 py-3.5 btn-glass btn-glass-submit text-base"
                >
                  Submit Inquiry
                  <Send size={18} className="ml-2 inline-block" />
                </motion.button>
              </form>
            )}
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default LeadForm;
